import {
  ActivateSMSNumberModalProps,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  activeCompanySelector,
  twilioNumbersToSelectSelector,
} from '@mm/state/selectors/viewSelectors';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useResolutionInfo } from '@mm/hooks/src/use-resolution-info';
import { selectCompanyOwner } from '@mm/state/selectors/userSelector';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
  enhancedBillingPagesSelector,
} from '@mm/state/selectors/facebookPageSelector';
import {
  enhancedPlansSelector,
  getCurrentSubscription,
} from '@mm/state/selectors/subscriptionSelectors';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useTwilioCRUDActions } from '@mm/state/actions/TwilioCRUDActions';
import { ArrowRightOutlined } from '@ant-design/icons';
import { ReactComponent as CheckIcon } from 'assets/check-circle-green.svg';
import { ReactComponent as SmartphoneIcon } from 'assets/smartphone.svg';
import { ReactComponent as BusinessIcon } from 'assets/business.svg';
import { ReactComponent as UserIcon } from 'assets/user-basic.svg';
import phoneCrystal from 'assets/phone-crystal.png';
import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { find } from 'lodash/fp';
import { get } from 'lodash';
import { StepperComponent } from '../../../dashboard/pages/SignupOnboarding/pages/NewUserInfoFlow/components/SetCompanyInfo/Stepper/StepperComponent';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import {
  CloseModalProps,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../modal-helpers';
import styles from './ActivateSMSNumberModal.module.css';
import { WrappedTextInput } from '../../components/WrappedTextInput/WrappedTextInput';
import {
  AreaCodeOption,
  countriesSelectorValues,
  PhoneNumberSelection,
} from '../../../editor/features/beta/modules/Settings/SMS/SMSActivationPage/PhoneNumberSelection/PhoneNumberSelection';
import { TestPhoneNumberForm } from '../../../editor/features/beta/modules/Settings/SMS/SMSActivationPage/TestPhoneNumberForm/TestPhoneNumberForm';

type StepType = {
  body: JSX.Element | null;
  footer?: string | null;
  onClick?: () => void;
  disabled?: boolean;
  hideHeader?: boolean;
  hideStepper?: boolean;
};

type FormValues = {
  businessEmail: string;
  phoneNumber: string;
  companyName: string;
  firstName: string;
  lastName: string;
};

const getValidationSchema = () => {
  return Yup.object().shape({
    businessEmail: Yup.string()
      .email('Invalid Email')
      .required('Email Required'),
    companyName: Yup.string().trim().required('Company Name Required'),
    firstName: Yup.string().trim().required('Full Name Required'),
    lastName: Yup.string().trim().required('Full Name Required'),
  });
};

const fields = {
  businessEmail: 'businessEmail',
  companyName: 'companyName',
  firstName: 'firstName',
  lastName: 'lastName',
};

const ActivateSMSNumberModal = (
  props: CloseModalProps & ActivateSMSNumberModalProps,
) => {
  const { closeModal, startStep, onSuccessActivateNumber, otherFacebookPage } =
    props;
  const modalActions = useModalActions();
  const [areaCode, setAreaCode] = useState<AreaCodeOption>();

  const { update: updateCompany } = useCompanyCRUDActions();
  const companyOwner = useSelector(selectCompanyOwner);
  const activeCompany = useSelector(activeCompanySelector);
  const { isMobile } = useResolutionInfo();
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const enhancedBillingPages = useSelector(enhancedBillingPagesSelector);
  const facebookPage = otherFacebookPage
    ? otherFacebookPage
    : activeFacebookPage
    ? activeFacebookPage
    : enhancedBillingPages[0];

  const enhancedPlans = useSelector(enhancedPlansSelector);
  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPage.id),
  );
  const [countryCode, setCountryCode] = useState(countriesSelectorValues[0]);
  const numbers = useSelector(twilioNumbersToSelectSelector, shallowEqual);
  const subscriptionCRUDActions = useSubscriptionCRUDActions();
  const twilioCRUDActions = useTwilioCRUDActions();
  const navigatedFrom = get(location, 'state.navigatedFrom', null);
  const { openActivateSMSNumberModal } = useModalActions();
  const subscription = useSelector(getCurrentSubscription(facebookPage.id));
  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;
  const disableSMSActivateButton = useMemo(
    () =>
      activeFacebookPagePlanInfo.isNewTeamPlan ||
      activeFacebookPagePlanInfo.isLegacyProPlan ||
      (activeFacebookPagePlanInfo.isPromoPlan &&
        !facebookPage?.redemption?.freeTrial) ||
      activeFacebookPagePlanInfo.isLegacyTeamPlan ||
      activeFacebookPagePlanInfo.isSpecialPro,
    [
      activeFacebookPagePlanInfo.isLegacyProPlan,
      activeFacebookPagePlanInfo.isLegacyTeamPlan,
      activeFacebookPagePlanInfo.isNewTeamPlan,
      activeFacebookPagePlanInfo.isPromoPlan,
      activeFacebookPagePlanInfo.isSpecialPro,
      facebookPage?.redemption?.freeTrial,
    ],
  );

  const isSubscriptionCanceled =
    subscription?.canceledAt && subscription?.state !== 'active';

  const isConfirmButtonDisabled = !!(
    disableSMSActivateButton ||
    activeFacebookPagePlanInfo.isSpecialTeam ||
    isSubscriptionCanceled
  );

  useEffect(() => {
    if (!currentSubscription) {
      subscriptionCRUDActions.getCurrentSubscription(facebookPage.id, false);
    }
  }, [facebookPage.id, currentSubscription, subscriptionCRUDActions]);

  const ativePlan = useMemo(() => {
    return find<EnhancedPlan>(['planCode', facebookPage?.plan?.planCode])(
      enhancedPlans,
    );
  }, [enhancedPlans, facebookPage?.plan?.planCode]);

  const [step, setStep] = useState(startStep || 0);
  const [phoneNumber, setPhoneNumber] = useState('');

  const onSubmit = async (formValues: FormValues) => {
    const { businessEmail, companyName, firstName, lastName } = formValues;

    if (activeCompany) {
      const { id, createdAt } = activeCompany;
      await updateCompany({
        id,
        createdAt,
        businessEmail: businessEmail.trim(),
        businessFirstName: firstName.trim(),
        businessLastName: lastName.trim(),
        businessName: companyName.trim(),
      });
    }
  };
  const validationSchema = getValidationSchema();
  const initialValues = {
    businessEmail: activeCompany?.businessEmail ?? '',
    companyName: activeCompany?.businessName ?? activeCompany?.name ?? '',
    firstName:
      activeCompany?.businessFirstName ?? companyOwner?.firstName ?? '',
    lastName: activeCompany?.businessLastName ?? companyOwner?.lastName ?? '',
  };
  const skipFetchActivePage = otherFacebookPage
    ? otherFacebookPage.id !== activeFacebookPage?.id
    : false;

  const getIsValid = (formProps: FormikProps<FormValues>) => {
    if (isMobile) {
      return false;
    }
    return !formProps.isValid;
  };

  const StepOne = () => {
    return (
      <div className={styles.instagramConnectModalStep}>
        <div className={styles.instagramConnectModalStepIntro}>
          SMS/MMS Sending Compliance
        </div>
        <div className={styles.instagramConnectModalStepText}>
          Cellular careers now require that any campaign sending SMS/MMS
          messages be registered for their standard: A2P 10DLC. Please register
          your account
        </div>
        <div>
          <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange={!isMobile}
            validateOnBlur={!isMobile}
          >
            {(formProps: FormikProps<FormValues>) => {
              const isAnyFieldEmpty =
                !formProps.values.businessEmail.length ||
                !formProps.values.lastName.length ||
                !formProps.values.firstName.length ||
                !formProps.values.companyName.length;
              return (
                <form
                  onSubmit={() => onSubmit(formProps.values)}
                  className={styles.instagramConnectModalForm}
                >
                  <div>
                    <WrappedTextInput
                      icon={<BusinessIcon />}
                      inputIcon={
                        !formProps.errors.companyName ? (
                          <CheckIcon className={styles.checkIcon} />
                        ) : undefined
                      }
                      label="Business Name"
                      formProps={formProps}
                      fieldName={fields.companyName}
                      placeholder="Enter your company name here"
                    />
                    <div className={styles.nameContainer}>
                      <div className={styles.label}>Full Name</div>
                      <div className={styles.nameWrapper}>
                        <WrappedTextInput
                          icon={<UserIcon />}
                          inputIcon={
                            !formProps.errors.firstName ? (
                              <CheckIcon className={styles.checkIcon} />
                            ) : undefined
                          }
                          formProps={formProps}
                          fieldName={fields.firstName}
                          placeholder="Enter your first name here"
                        />
                        <WrappedTextInput
                          formProps={formProps}
                          inputIcon={
                            !formProps.errors.lastName ? (
                              <CheckIcon className={styles.checkIcon} />
                            ) : undefined
                          }
                          fieldName={fields.lastName}
                          placeholder="Enter your last name here"
                        />
                      </div>
                    </div>

                    <WrappedTextInput
                      icon={<UserIcon />}
                      inputIcon={
                        !formProps.errors.businessEmail &&
                        formProps.values.businessEmail.length ? (
                          <CheckIcon className={styles.checkIcon} />
                        ) : undefined
                      }
                      label="Email"
                      formProps={formProps}
                      fieldName={fields.businessEmail}
                      placeholder="Enter your email here"
                    />
                  </div>
                  <PrimaryButton
                    color={ButtonColor.blue}
                    className={styles.instagramConnectModalSubmitButton}
                    disabled={getIsValid(formProps) || isAnyFieldEmpty}
                    onClick={async () => {
                      await onSubmit(formProps.values);
                      setTimeout(() => {
                        setStep(step + 1);
                      }, 300);
                    }}
                  >
                    <ArrowRightOutlined className={styles.arrowIcon} />
                    Confirm
                  </PrimaryButton>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>
    );
  };

  const StepTwo = (props: {
    initialAreaCode?: AreaCodeOption;
    initialSearchedForNumber?: boolean;
  }) => {
    return (
      <div className={styles.instagramConnectModalStepSecond}>
        <div className={styles.instagramConnectModalStepIntro}>
          Phone number registration
        </div>
        <PhoneNumberSelection
          onSearchClick={() => {
            if (step === 1) {
              setStep(step + 1);
            }
            if (areaCode) {
              twilioCRUDActions.getAvailableNumbers(
                facebookPage.id,
                countryCode.code,
                areaCode.code,
                4,
              );
            }
          }}
          hidePhoneNumberSelection={step === 1}
          initialAreaCode={props.initialAreaCode}
          onAreaCodeChange={code => setAreaCode(code)}
          onCountryCodeChange={code => setCountryCode(code)}
          stayOnPage
          onPhoneSelect={(phoneNumber: string) => {
            setPhoneNumber(phoneNumber);
          }}
          showSearchIcon
          selectedPhoneNumber={phoneNumber}
          customPhoneNumbers={numbers}
          facebookPageOverride={facebookPage}
          skipFetchActivePage={skipFetchActivePage}
          countryCodeOverride={countryCode}
        />
      </div>
    );
  };

  const onClickStep = async (phoneNumber: string) => {
    if (facebookPage && facebookPage.plan && ativePlan) {
      modalActions.openBillingModal({
        facebookPage,
        plan: ativePlan,
        phoneNumber,
        subscription: currentSubscription,
        navigatedFrom,
        smsActivation: true,
        stayOnPage: true,
        skipFetchActivePage,
        onSuccessBilling: () => {
          openActivateSMSNumberModal({
            onSuccessActivateNumber,
            otherFacebookPage,
            startStep: 3,
          });
        },
      });
    } else {
      if (facebookPage && phoneNumber && facebookPage?.redemption?.freeTrial) {
        await twilioCRUDActions.activatePage(facebookPage.id, false, {
          numberKind: 'twilio',
          number: phoneNumber,
          skipFetchActivePage,
        });
        setStep(3);
      }
    }
  };

  const StepFour = () => {
    return (
      <div className={styles.instagramConnectModalStep}>
        <TestPhoneNumberForm
          className={styles.testPhoneNumberForm}
          facebookPage={facebookPage}
          showSelectedLabel
        />
      </div>
    );
  };

  const CongratulationsStep = () => {
    return (
      <div className={styles.instagramConnectModalCongratulationsStep}>
        <img src={phoneCrystal} className={styles.instagramConnectModalImgIg} />
        <div className={styles.instagramConnectModalStepHeader}>
          Congratulations!
        </div>
        <div className={styles.instagramConnectModalStepText}>
          Now let's build your first SMS automation
        </div>
      </div>
    );
  };

  const steps: StepType[] = [
    {
      body: <StepOne />,
    },
    {
      body: <StepTwo initialAreaCode={areaCode} />,
    },
    {
      body: <StepTwo initialAreaCode={areaCode} initialSearchedForNumber />,
      footer: 'Confirm',
      onClick: () => {
        onClickStep(phoneNumber);
      },
      disabled: !phoneNumber,
    },
    {
      body: <StepFour />,
      footer: 'Confirm',
      disabled: isConfirmButtonDisabled,
      onClick: () => {
        setStep(step + 1);
      },
    },
    {
      body: <CongratulationsStep />,
      footer: 'Continue',
      onClick: () => {
        closeModal();
        onSuccessActivateNumber?.();
      },
      hideHeader: true,
      hideStepper: true,
    },
  ];

  return (
    <div className={styles.instagramConnectModal}>
      <ModalHeader
        closeOverride={closeModal}
        childrenContainerClassName={styles.instagramConnectModalHeaderContent}
        className={styles.instagramConnectModalHeader}
      >
        {!steps[step]?.hideHeader && (
          <>
            <SmartphoneIcon
              className={styles.instagramConnectModalHeaderIcon}
            />
            Claim Phone Number
          </>
        )}
      </ModalHeader>

      <ModalBody className={styles.instagramConnectModalBody}>
        {!steps[step]?.hideStepper && (
          <StepperComponent
            size={4}
            activeStep={step + 1}
            css={{ container: styles.instagramConnectModalStepper }}
            isTransparent
          />
        )}
        {steps[step]?.body}
      </ModalBody>

      {steps[step]?.footer && (
        <ModalFooter className={styles.instagramConnectModalFooter}>
          <PrimaryButton
            color={ButtonColor.blue}
            className="instagram-connect-modal__button"
            onClick={steps[step]?.onClick}
            disabled={steps[step]?.disabled}
          >
            <ArrowRightOutlined className={styles.arrowIcon} />
            {steps[step]?.footer}
          </PrimaryButton>
        </ModalFooter>
      )}
    </div>
  );
};

export default ActivateSMSNumberModal;
